import { ExpandedState } from "@tanstack/react-table";
import {
    Form1099ReportRow,
    Form1099ReportTransactionLine,
} from "@common/schemas/domains/tax/form1099-report.schema";
import { toWorkspaceDate } from "@common/helpers/date";
import { WorkspaceTimezone } from "@common/workspaceTimezone";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";

export const FORM_1099_NEC_REPORT_LABEL = "1099 Transaction Detail";
export const FORM_1099_NEC_REPORT_DESCRIPTION =
    "1099 transactions and totals grouped by vendor for a tax year, with a Track1099 CSV export";

export type Form1099DetailRowKind = "vendor" | "transaction" | "closingBalance";

export interface Form1099NecRow {
    id: string;
    rowKind: Form1099DetailRowKind;
    entityId?: number;
    vendor?: Form1099ReportRow;
    vendorName?: string;
    closingBalance?: number;
    balanceValue?: number;
    transaction?: Form1099ReportTransactionLine;
    children?: Form1099NecRow[];
}

export interface Form1099TableCallbacks {
    onSourceClick: (transaction: Form1099ReportTransactionLine) => void;
    onReferenceClick: (transaction: Form1099ReportTransactionLine) => void;
}

export type Form1099TableMeta = ViewTableMeta<Form1099TableCallbacks, unknown>;

export function formatForm1099Recipient(row: Form1099ReportRow): string {
    const personName = [row.firstName, row.lastName].filter(Boolean).join(" ");
    return [row.businessName?.trim(), personName].find(Boolean) ?? "";
}

interface Form1099EntityRows {
    entityId: number;
    rows: Form1099ReportRow[];
}

interface BuildVendorRowParams {
    entityId: number;
    vendor: Form1099ReportRow;
    transactions: Form1099ReportTransactionLine[];
}

function buildVendorRow({
    entityId,
    vendor,
    transactions,
}: BuildVendorRowParams): Form1099NecRow {
    const keyPrefix = `${entityId}:${vendor.counterpartyId}`;
    const lastTransaction = transactions.at(-1);
    const closingBalance = lastTransaction
        ? lastTransaction.balance
        : Number(vendor.box1);

    const transactionRows = transactions.map(
        (transaction, index): Form1099NecRow => ({
            id: `1099:txn:${keyPrefix}:${transaction.id}:${index}`,
            rowKind: "transaction",
            transaction,
        }),
    );
    const closingRow: Form1099NecRow = {
        id: `1099:closing:${keyPrefix}`,
        rowKind: "closingBalance",
        balanceValue: closingBalance,
    };

    return {
        id: `1099:vendor:${keyPrefix}`,
        rowKind: "vendor",
        entityId,
        vendor,
        vendorName: formatForm1099Recipient(vendor),
        closingBalance,
        children: [...transactionRows, closingRow],
    };
}

export function toForm1099NecRows(
    entityRows: Form1099EntityRows[],
): Form1099NecRow[] {
    return entityRows.flatMap(({ entityId, rows }) =>
        rows.map((vendor) =>
            buildVendorRow({
                entityId,
                vendor,
                transactions: vendor.transactions ?? [],
            }),
        ),
    );
}

function withRunningBalances(
    transactions: Form1099ReportTransactionLine[],
): Form1099ReportTransactionLine[] {
    let balanceCents = 0;
    return transactions.map((transaction) => {
        balanceCents += Math.round(transaction.amount * 100);
        return { ...transaction, balance: balanceCents / 100 };
    });
}

export function getForm1099DefaultExpandedState(
    rows: Form1099NecRow[],
): Record<string, boolean> {
    return rows.reduce<Record<string, boolean>>((state, row) => {
        state[row.id] = true;
        return state;
    }, {});
}

interface MergeForm1099ExpandedStateParams {
    current: ExpandedState;
    rows: Form1099NecRow[];
    previousRowIds: ReadonlySet<string>;
}

export function mergeForm1099ExpandedState({
    current,
    rows,
    previousRowIds,
}: MergeForm1099ExpandedStateParams): ExpandedState {
    if (current === true) {
        return current;
    }
    return rows.reduce<Record<string, boolean>>((state, row) => {
        if (!previousRowIds.has(row.id) || current[row.id]) {
            state[row.id] = true;
        }
        return state;
    }, {});
}

export function filterForm1099NecRows(
    rows: Form1099NecRow[],
    search: string,
): Form1099NecRow[] {
    const term = search.trim().toLowerCase();
    if (term === "") {
        return rows;
    }
    return rows.flatMap((row) => {
        if ((row.vendorName ?? "").toLowerCase().includes(term)) {
            return [row];
        }
        if (!row.vendor || row.entityId === undefined) {
            return [];
        }
        const matchingTransactions = (row.vendor.transactions ?? []).filter(
            (transaction) => isTransactionMatch(transaction, term),
        );
        if (matchingTransactions.length === 0) {
            return [];
        }
        return [
            buildVendorRow({
                entityId: row.entityId,
                vendor: row.vendor,
                transactions: withRunningBalances(matchingTransactions),
            }),
        ];
    });
}

function isTransactionMatch(
    transaction: Form1099ReportTransactionLine,
    term: string,
): boolean {
    return [
        transaction.bankDescription ?? "",
        transaction.description ?? "",
        transaction.accountName,
        transaction.splitLabel,
        transaction.referenceNumber,
    ].some((value) => value.toLowerCase().includes(term));
}

const TAX_YEAR_OPTION_COUNT = 5;

export function getWorkspaceCurrentYear(timezone: WorkspaceTimezone): number {
    const workspaceToday = toWorkspaceDate({ date: new Date(), timezone });
    return Number.parseInt(workspaceToday.slice(0, 4), 10);
}

interface TaxYearItem {
    value: number;
    label: string;
}

interface BuildTaxYearItemsParams {
    currentYear: number;
    bookkeepingStartDate?: string | null;
}

export function parseBookkeepingStartYear(
    bookkeepingStartDate: string | null | undefined,
): number | null {
    if (!bookkeepingStartDate) {
        return null;
    }
    const year = Number.parseInt(bookkeepingStartDate.slice(0, 4), 10);
    return Number.isNaN(year) ? null : year;
}

export function buildTaxYearItems({
    currentYear,
    bookkeepingStartDate,
}: BuildTaxYearItemsParams): TaxYearItem[] {
    const startYear = parseBookkeepingStartYear(bookkeepingStartDate);
    const earliestOfferedYear = currentYear - TAX_YEAR_OPTION_COUNT + 1;
    const minYear =
        startYear !== null
            ? Math.max(earliestOfferedYear, startYear)
            : earliestOfferedYear;
    const count = Math.max(1, currentYear - minYear + 1);

    return Array.from({ length: count }, (_, index) => {
        const year = currentYear - index;
        return { value: year, label: String(year) };
    });
}

interface GetDefaultTaxYearParams {
    taxYearItems: TaxYearItem[];
    currentYear: number;
}

export function getDefaultTaxYear({
    taxYearItems,
    currentYear,
}: GetDefaultTaxYearParams): number {
    return (
        taxYearItems.find((item) => item.value === currentYear - 1)?.value ??
        taxYearItems[0].value
    );
}
